import loginScreenImageUrl from "@/assets/login-screen.svg"
import { Button } from "@/components/chadcn/Button"
import HeroImage from "@/components/HeroImage"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useSelfRegisterDeviceMutation } from "@/data/queries"
import { ROUTES } from "@/routes"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router"

type CheckSmsState = {
  deviceCode?: string
  phoneNumber?: string
}

const CheckSmsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state as CheckSmsState | null) ?? {}
  const { deviceCode, phoneNumber } = state

  const { mutateAsync: selfRegisterDevice, isPending: isResending } =
    useSelfRegisterDeviceMutation()
  const [hasResent, setHasResent] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)

  useEffect(() => {
    if (!deviceCode || !phoneNumber) {
      navigate(ROUTES.SIGN_IN)
    }
  }, [deviceCode, phoneNumber, navigate])

  useEffect(() => {
    if (!showSuccessMessage) return
    const timeoutId = setTimeout(() => setShowSuccessMessage(false), 3000)
    return () => clearTimeout(timeoutId)
  }, [showSuccessMessage])

  const handleResend = async () => {
    if (!deviceCode || !phoneNumber) return
    setHasResent(true)
    try {
      await selfRegisterDevice({ deviceCode, phoneNumber })
      setShowSuccessMessage(true)
    } catch {
      // ignore — leave button as "Text message sent" to match OLD behavior
    }
  }

  const handleContactBloomlife = () => {
    navigate(ROUTES.CONTACT_BLOOMLIFE, { state: { showBackButton: false } })
  }

  return (
    <Page showAppbar={false}>
      <HeroImage src={loginScreenImageUrl} />
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-h1">We&apos;ve sent you a text</h1>
          <h2 className="text-h2 text-primary-contrast/70">
            Please check your text messages for a login link. If you don&apos;t receive a text
            message in 5 minutes, please contact Bloomlife
          </h2>
        </div>

        {showSuccessMessage && (
          <div
            role="status"
            className="rounded-md border border-primary/40 bg-primary/10 p-3 text-sm text-primary-contrast"
          >
            Text message sent successfully!
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Button
            type="button"
            disabled={isResending || hasResent}
            onClick={handleResend}
            className="flex w-full gap-3 py-2 text-base"
          >
            {isResending && <Spinner />}
            {isResending ? "Resending..." : hasResent ? "Text message sent" : "Resend text message"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleContactBloomlife}
            className="flex w-full gap-3 py-2 text-base"
          >
            Contact Bloomlife
          </Button>
        </div>
      </div>
    </Page>
  )
}

export default CheckSmsPage
